/**
 * The 7d change shown for a market.
 *
 * Uses `change_week_fair`: the fair band's level (the median of recent
 * cross-market sales, daily buckets) today against seven days ago. It is the
 * number that lands nearest what other marketplaces print for the week, and
 * it does not print +4013% on a collection with one sale the way the
 * escrow-only VWAP did. Legacy change_week is VOLUME growth and is never
 * used here. Missing data stays null, not 0.
 */
export function indexedSaleChange(market: {
 change_week_fair?: number | null
 change_week_vwap?: number | null
}): number | null {
 // Fair only. The escrow-only VWAP fallback printed +370% for DOG while its
 // fair price fell 28%, and 0% for markets with no history at all; a market
 // the keeper has not priced reads N/A, as on the homepage.
 const fair = market.change_week_fair
 return typeof fair === 'number' && Number.isFinite(fair) ? fair : null
}

export function formatSaleChange(value: number | null): string {
 if (value === null || !Number.isFinite(value)) return 'N/A'
 const normalized = Math.abs(value) < 0.005 ? 0 : value
 return `${normalized > 0 ? '+' : ''}${normalized.toFixed(Math.abs(normalized) < 0.1 ? 2 : 1)}%`
}

export interface SaleChangeSnapshot {
 change: number | null
 reason: string
 metric: 'sale_price_change_7d'
 source: 'cross_market_ask_sales'
 method: 'first_last_daily_average'
 status: 'available' | 'insufficient_history'
 window: { start: number; end: number }
 fetchedAt: string
 first?: { time: number; vwap: number }
 last?: { time: number; vwap: number }
}

/**
 * What one of a market is worth: its fair price or its floor, whichever is
 * lower (per unit for tokens) -- the one price the site shows for a market,
 * the same rule the wallet values holdings by and listing flows suggest.
 * Either alone when only one is known; null when neither is.
 */
export function marketWorth(
 market: { fair_sats?: number | null; floor_price?: number | null; floor_price_per?: number | null },
 fungible: boolean
): number | null {
 const pos = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null)
 const fair = pos(market.fair_sats)
 const floor = pos(fungible ? market.floor_price_per ?? market.floor_price : market.floor_price)
 if (fair !== null && floor !== null) return Math.min(fair, floor)
 return fair ?? floor
}

/** The fair price's 24h change (`change_day_fair`), or null. */
export function fairDayChange(market: { change_day_fair?: number | null }): number | null {
 const v = market.change_day_fair
 return typeof v === 'number' && Number.isFinite(v) ? v : null
}
